import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Grid,
  Icon,
  InputBase,
  Tab,
  Tabs,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import HttpHandler, { getModelChangeEvent } from 'app/reusable/HttpHandler'
import { useModal, useDismissModal } from 'app/ducks/modal/hooks'
import { useHasPermission, useIsUser } from 'app/ducks/auth/hooks'
import { USER_TYPE } from 'app/ducks/auth/types'
import useIsMobile from 'app/hooks/useIsMobile'
import UserTypeSpecific from 'app/components/UserTypeSpecific/UserTypeSpecific'
import RecordFormModal from 'app/components/RecordFormModal/RecordFormModal'
import {
  PAGE_PARAM,
  SEARCH_PARAM,
} from 'app/components/ModelListPage/listQuery'
import { SEARCH_DEBOUNCE_MS } from 'app/components/ModelListPage/useModelList'
import KPICard from '../dashboard/KPICard'
import ActivityClass from '../gym/ActivityClass'
import ActivityClassUser from '../users/ActivityClassUser'
import ActivityClassListPage from './ActivityClassListPage'
import { ActivityModalityDetails } from './ActivityModality'
import ModalityCard from './ModalityCard'
import {
  CATEGORY_MODEL,
  CategorySelector,
  ModalityCategoriesManager,
  useModalityCategories,
} from './ModalityCategories'
import { CATEGORY_PARAM, categoryParam } from './modalityCategory'
import useFeatureFlag from 'app/ducks/featureFlags/hooks'
import TeacherArea, { PERIOD_PARAM, TEACHER_PARAM } from './TeacherArea'
import { TEACHER_AREA_FLAG } from './teacherAreaFlag'

const APP = 'activities'
const MODALITY_MODEL = 'activity-modality'
const PAGE_SIZE = 24

export const TAB_PARAM = 'aba'
export const CLASSES_TAB = 'turmas'
const MODALITIES_TAB = 'modalidades'
export const TEACHER_TAB = 'professor'
// The class list's modality filter (`ActivityClassListPage`'s filter key).
const MODALITY_FILTER_PARAM = 'modalidade'

const useStyles = makeStyles(({ palette }) => ({
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  title: { margin: 0, fontSize: 22, fontWeight: 700 },
  subtitle: { margin: '4px 0 0', color: palette.text.secondary },
  tabs: { marginTop: 12, borderBottom: `1px solid ${palette.divider}` },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    margin: '24px 0 16px',
  },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  search: {
    flex: '0 1 320px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    backgroundColor: palette.action.hover,
    '& .material-icons': { fontSize: 20, color: palette.text.secondary },
  },
  searchMobile: { flex: '1 1 100%', order: 3 },
  count: {
    marginLeft: 'auto',
    fontSize: 13,
    fontWeight: 600,
    color: palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  empty: {
    padding: '48px 16px',
    textAlign: 'center',
    color: palette.text.secondary,
    '& .material-icons': { fontSize: 40, marginBottom: 8 },
  },
  grid: { transition: 'opacity 150ms ease-in' },
  gridLoading: { opacity: 0.5, pointerEvents: 'none' },
}))

/**
 * Modalities (with their indicators) of the selected category, as a grid
 * of cards, searchable and loaded `PAGE_SIZE` at a time.
 */
const useModalities = ({ category, text }) => {
  const [rows, setRows] = useState(null)
  const [paginator, setPaginator] = useState(null)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)

  const fetchPage = useCallback(
    (pageToLoad) =>
      HttpHandler.list(APP, MODALITY_MODEL, {
        insights: true,
        category: categoryParam(category),
        text_filter: text || undefined,
        // Active ones first.
        order_by: 'status,description',
        paginate_by: PAGE_SIZE,
        page: pageToLoad,
      }).then(({ data }) => data),
    [category, text]
  )

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    fetchPage(1)
      .then((data) => {
        if (cancelled) return
        setRows(data?.data || [])
        setPaginator(data?.paginator || null)
        setPage(1)
      })
      .catch(() => {
        if (cancelled) return
        setRows((current) => current || [])
        toast.error('Erro ao buscar as modalidades.')
      })
      .finally(() => !cancelled && setIsLoading(false))
    return () => {
      cancelled = true
    }
  }, [fetchPage])

  useEffect(() => reload(), [reload])

  useEffect(() => {
    const { type } = getModelChangeEvent(APP, MODALITY_MODEL)
    const onChange = () => reload()
    window.addEventListener(type, onChange)
    return () => window.removeEventListener(type, onChange)
  }, [reload])

  const loadMore = () => {
    setIsLoading(true)
    fetchPage(page + 1)
      .then((data) => {
        setRows((current) => [...(current || []), ...(data?.data || [])])
        setPaginator(data?.paginator || null)
        setPage(page + 1)
      })
      .catch(() => toast.error('Erro ao buscar as modalidades.'))
      .finally(() => setIsLoading(false))
  }

  return {
    rows,
    count: paginator?.count ?? 0,
    hasMore: !!paginator && page < (paginator.last_page_number || 1),
    isLoading,
    loadMore,
  }
}

const ModalitiesOverview = ({ category, onShowClasses }) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const showModal = useModal()
  const dismissModal = useDismissModal()
  const isStaff = useIsUser([
    USER_TYPE.ADMINISTRATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.COLLABORATOR,
  ])
  const canAdd = useHasPermission(MODALITY_MODEL, 'add') && isStaff
  const canChange = useHasPermission(MODALITY_MODEL, 'change') && isStaff

  const [searchText, setSearchText] = useState('')
  const [text, setText] = useState('')
  useEffect(() => {
    const timeout = setTimeout(() => setText(searchText), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [searchText])

  const modalities = useModalities({ category, text })
  const kpiParams = useMemo(
    () => (category ? { category: categoryParam(category) } : {}),
    [category]
  )

  const openForm = (id) =>
    showModal({
      title: id ? 'Editar modalidade' : 'Nova modalidade',
      children: (
        <RecordFormModal
          djangoApp={APP}
          djangoModel={MODALITY_MODEL}
          id={id}
          onSuccess={dismissModal}
        />
      ),
    })

  const openDetails = (modality) =>
    showModal({
      title: modality.description,
      children: <ActivityModalityDetails id={modality.id} />,
    })

  const { rows, count, hasMore, isLoading, loadMore } = modalities

  return (
    <>
      <UserTypeSpecific type={[USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR]}>
        {/* KPICard fetches once: remount it for every category. */}
        <Grid container spacing={3} className="mt-2" key={category || 'all'}>
          <Grid item xs={12} md={4}>
            <KPICard
              app={APP}
              name="activity-classes-count"
              itemComponent={ActivityClass}
              params={kpiParams}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <KPICard
              app={APP}
              name="activity-classes-users"
              itemComponent={ActivityClassUser}
              params={kpiParams}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <KPICard
              app={APP}
              name="activity-classes-billing-users"
              itemComponent={ActivityClassUser}
              params={kpiParams}
            />
          </Grid>
        </Grid>
      </UserTypeSpecific>

      <div className={classes.toolbar}>
        <h2 className={classes.sectionTitle}>Modalidades</h2>
        <label
          className={clsx(classes.search, isMobile && classes.searchMobile)}
        >
          <Icon aria-hidden>search</Icon>
          <InputBase
            fullWidth
            type="search"
            value={searchText}
            placeholder="Buscar modalidades…"
            inputProps={{ 'aria-label': 'Buscar modalidades' }}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </label>
        <span className={classes.count} aria-live="polite">
          {rows !== null &&
            `${count} ${count === 1 ? 'modalidade' : 'modalidades'}`}
        </span>
        {canAdd && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icon>add</Icon>}
            onClick={() => openForm()}
          >
            Nova modalidade
          </Button>
        )}
      </div>

      {rows === null ? (
        <div className="text-center py-8">
          <CircularProgress />
        </div>
      ) : rows.length ? (
        <>
          <Grid
            container
            spacing={3}
            className={clsx(classes.grid, isLoading && classes.gridLoading)}
          >
            {rows.map((modality) => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={modality.id}>
                <ModalityCard
                  modality={modality}
                  canEdit={canChange}
                  onOpen={() => openDetails(modality)}
                  onEdit={() => openForm(modality.id)}
                  onShowClasses={() => onShowClasses(modality)}
                />
              </Grid>
            ))}
          </Grid>
          {hasMore && (
            <div className="text-center mt-6">
              <Button
                variant="outlined"
                color="primary"
                disabled={isLoading}
                onClick={loadMore}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Carregar mais'}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className={classes.empty}>
          <Icon aria-hidden>sports</Icon>
          <p className="m-0">
            {text
              ? 'Nenhuma modalidade encontrada para essa busca.'
              : category
              ? 'Nenhuma modalidade nesta categoria.'
              : 'Nenhuma modalidade cadastrada.'}
          </p>
        </div>
      )}
    </>
  )
}

/**
 * The Modalidades page (`/schools`) behind `new_modalities_page`: a
 * category selector that scopes everything below it, and tabs — the
 * modalities as cards with their indicators, the class list with a filter
 * per field and, behind `teacher_area`, the teacher's own area
 * (`TeacherArea`) — the only tab a collaborator gets while that flag is on. Category and tab live in the URL (`categoria`, `aba`).
 */
const ModalitiesPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const isMobile = useIsMobile()
  const showModal = useModal()
  const categories = useModalityCategories()

  const isStaff = useIsUser([
    USER_TYPE.ADMINISTRATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.COLLABORATOR,
  ])
  const canAddCategory = useHasPermission(CATEGORY_MODEL, 'add') && isStaff
  const canChangeCategory =
    useHasPermission(CATEGORY_MODEL, 'change') && isStaff
  const canDeleteCategory =
    useHasPermission(CATEGORY_MODEL, 'delete') && isStaff
  const canManageCategories =
    canAddCategory || canChangeCategory || canDeleteCategory

  const showTeacherArea = useFeatureFlag(TEACHER_AREA_FLAG)
  const isCollaborator = useIsUser([USER_TYPE.COLLABORATOR])
  // With the teacher area on, a collaborator (the teacher) only gets their
  // own area; the modality overview and the full class list stay with
  // managers and administrators.
  const teacherAreaOnly = showTeacherArea && isCollaborator

  const params = new URLSearchParams(location.search)
  const category = params.get(CATEGORY_PARAM) || undefined
  const tabs = teacherAreaOnly
    ? [TEACHER_TAB]
    : [MODALITIES_TAB, CLASSES_TAB, ...(showTeacherArea ? [TEACHER_TAB] : [])]
  const tab = tabs.includes(params.get(TAB_PARAM))
    ? params.get(TAB_PARAM)
    : tabs[0]

  const navigate = (update) => {
    const next = new URLSearchParams(location.search)
    update(next)
    history.replace({ pathname: location.pathname, search: next.toString() })
  }

  // A new category starts the class list over: its search, page and
  // modality filter belonged to the previous one.
  const changeCategory = (value) =>
    navigate((next) => {
      ;[SEARCH_PARAM, PAGE_PARAM, MODALITY_FILTER_PARAM].forEach((key) =>
        next.delete(key)
      )
      if (value) next.set(CATEGORY_PARAM, value)
      else next.delete(CATEGORY_PARAM)
    })

  // Each tab's own params (search, page, the teacher area's teacher and
  // period) don't carry over to the next one.
  const changeTab = (value) =>
    navigate((next) => {
      ;[SEARCH_PARAM, PAGE_PARAM, TEACHER_PARAM, PERIOD_PARAM].forEach((key) =>
        next.delete(key)
      )
      if (value === MODALITIES_TAB) next.delete(TAB_PARAM)
      else next.set(TAB_PARAM, value)
    })

  const showClassesOf = (modality) =>
    navigate((next) => {
      next.delete(SEARCH_PARAM)
      next.delete(PAGE_PARAM)
      next.set(TAB_PARAM, CLASSES_TAB)
      next.set(MODALITY_FILTER_PARAM, String(modality.id))
    })

  const manageCategories = () =>
    showModal({
      title: 'Categorias de modalidade',
      children: (
        <ModalityCategoriesManager
          canAdd={canAddCategory}
          canChange={canChangeCategory}
          canDelete={canDeleteCategory}
        />
      ),
    })

  return (
    <>
      <div className="m-sm-30 mb-0">
        <div className={classes.header}>
          <div>
            <h1 className={classes.title}>Modalidades</h1>
            <p className={classes.subtitle}>
              {teacherAreaOnly
                ? 'Suas turmas, chamadas e indicadores por categoria de modalidade'
                : 'Indicadores, turmas e chamadas por categoria de modalidade'}
            </p>
          </div>
          {canManageCategories && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Icon>category</Icon>}
              onClick={manageCategories}
              fullWidth={isMobile}
            >
              Gerenciar categorias
            </Button>
          )}
        </div>

        {categories === null ? (
          <CircularProgress size={20} />
        ) : (
          <CategorySelector
            categories={categories}
            value={category}
            onChange={changeCategory}
            isMobile={isMobile}
          />
        )}

        <Tabs
          value={tab}
          onChange={(event, value) => changeTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? 'fullWidth' : 'standard'}
          className={classes.tabs}
        >
          {!teacherAreaOnly && (
            <Tab label="Modalidades" value={MODALITIES_TAB} />
          )}
          {!teacherAreaOnly && <Tab label="Turmas" value={CLASSES_TAB} />}
          {showTeacherArea && (
            <Tab label="Área do professor" value={TEACHER_TAB} />
          )}
        </Tabs>

        {tab === MODALITIES_TAB && (
          <ModalitiesOverview
            category={category}
            onShowClasses={showClassesOf}
          />
        )}
      </div>

      {/* `ModelListPage` brings its own page margin. */}
      {tab === CLASSES_TAB && (
        <ActivityClassListPage
          key={category || 'all'}
          category={category}
          extendedFilters
        />
      )}
      {tab === TEACHER_TAB && <TeacherArea category={category} />}
    </>
  )
}

export default ModalitiesPage
